import {Component, computed, inject, input, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import hanie from 'hare-niemeyer';

import {PermissionsService} from '@intocare/auth-data';
import {Statistics} from '@intocare/statistics';

import {StatisticsFacadeService} from './statistics-facade.service';
import {GraphStatistics} from '../../entities/graph-statistics.interface';
import {TaskStatistics} from '../../entities/task-statistics.interface';

@Component({
	selector: 'ic-task-overview-card',
	templateUrl: './task-overview.component.html',
	styleUrls: ['./task-overview.component.scss'],
	standalone: false
})
export class TaskOverviewComponent {
	dateInput = input<string>(undefined);
	resident = input<string>(undefined);
	protected date = signal<string>(undefined);
	protected computedDate = computed<string>(() => {
		return this.dateInput() ? this.dateInput() : this.date();
	});

	protected statisticsFacadeService = inject(StatisticsFacadeService);
	private translateService = inject(TranslateService);
	private permissionsService = inject(PermissionsService);

	private hasResidentOverviewPermission = this.permissionsService.userHasPermissions(
		'PORTAL.RESIDENT_OVERVIEW.TASK_STATISTICS_WIDGET'
	);
	private hasDashboardPermission = this.permissionsService.userHasPermissions('PORTAL.TASKS_WIDGET');

	canViewTaskStatisticsWidget = computed(
		() => (this.resident() && this.hasResidentOverviewPermission) || (!this.resident() && this.hasDashboardPermission)
	);

	statisticsQuery = injectQuery(
		this.statisticsFacadeService.getStatisticsQueryOptions(
			this.resident,
			this.computedDate,
			this.canViewTaskStatisticsWidget
		)
	);

	private taskStatistics = computed(() => {
		const statistics = this.statisticsQuery.data();
		return this.convertStatisticsToTaskStatistics(statistics);
	});

	completed = computed(() => this.calculateCompleted(this.taskStatistics()));

	graphStatistics = computed<GraphStatistics[]>(() => {
		const taskStatistics = this.taskStatistics();
		const {uncompleted, executed, skipped, unplanned} = this.getPercentages(taskStatistics);

		const translations = this.translateService.instant('labels.state');
		return [
			{
				class: 'thm-accent',
				name: translations.completed,
				value: taskStatistics.executed,
				percentage: executed
			},
			{
				class: 'thm-blue',
				name: translations.unplanned,
				value: taskStatistics.unplanned,
				percentage: unplanned
			},
			{
				class: 'thm-dark',
				name: translations.skipped,
				value: taskStatistics.skipped,
				percentage: skipped
			},
			{
				class: 'thm-grey',
				name: translations.uncompleted,
				value: taskStatistics.uncompleted,
				percentage: uncompleted
			}
		];
	});

	private calculateCompleted = (taskStatistics: TaskStatistics): number => {
		const unfinished = taskStatistics.uncompleted;
		const finished = taskStatistics.executed;
		const unplanned = taskStatistics.unplanned;
		const skipped = taskStatistics.skipped;

		// Total value displayed in donut
		const totalTasks = unfinished + finished + unplanned + skipped;
		const finishedTasksPercentage = ((totalTasks - unfinished) / totalTasks) * 100;

		return finishedTasksPercentage ? finishedTasksPercentage : 0;
	};

	private convertStatisticsToTaskStatistics = (statistics: Statistics | undefined): TaskStatistics => {
		if (!statistics) {
			return {
				executed: 0,
				skipped: 0,
				uncompleted: 0,
				unplanned: 0
			};
		}

		return {
			executed: statistics.task_executed - statistics.task_executed_unplanned,
			skipped: statistics.task_completed - statistics.task_executed,
			uncompleted: statistics.task_total - statistics.task_completed,
			unplanned: statistics.task_executed_unplanned
		};
	};

	private getPercentages(taskStatistics: TaskStatistics) {
		const sum = Object.keys(taskStatistics).reduce(
			(total, key: keyof TaskStatistics) => total + taskStatistics[key],
			0
		);

		if (sum === 0) {
			// The algorithm doesn't work if the sum of the values is equal to 0
			return taskStatistics;
		}

		// Use the largest-remainder algorithm to not end up with decimal percentages. See https://en.wikipedia.org/wiki/Largest_remainder_method.
		return hanie(taskStatistics, 100, false);
	}
}
